import {
  IApiRequest,
  ICreateDocumentCategoryRequest,
  IDocumentCategoriesResponse,
  IToastMessageConfig,
} from "@miplatform/types";
import {
  API_VERSION,
  DOCUMENT_SERVICE,
  X_API_VERSION,
} from "../../../constants";
import { apiOptions, apiRequest } from "../../../utils";

const API_DOCUMENT_TYPES = `${DOCUMENT_SERVICE}/DocumentCategories`;

/**
 * Service for handling document categories.
 *
 * @namespace documentCategories
 */
export const documentCategories = {
  /**
   * Fetches all document categories with optional filters and sorting.
   *
   * @function getAll
   * @memberof documentCategories
   * @param {Object} [params] - Optional parameters.
   * @param {Object} [params.filters] - Filters to apply to the request.
   * @param {Object} [params.sort] - Sorting options.
   * @returns {Promise<Object>} The response from the API.
   */
  getAll: async ({
    filters,
    sort = "dependentType asc",
    top,
    skip,
  }: IApiRequest = {}) => {
    const options = await apiOptions({
      url: `${API_DOCUMENT_TYPES}`,
      method: "get",
      headers: { [X_API_VERSION]: API_VERSION },
      filters,
      sort,
      top,
      skip,
    });
    return apiRequest(options);
  },

  /**
   * Fetches all document categories belonging to a dependent type.
   *
   * @function getByDependentType
   * @memberof documentCategories
   * @param {Object} [params] - Optional parameters.
   * @param {Object} [params.filters] - Filters to apply to the request.
   * @param {Object} [params.sort] - Sorting options.
   * @returns {Promise<Object>} The response from the API.
   */
  getByDependentType: async ({
    entityType,
    sort = "name asc",
    top,
    skip,
  }: IDocumentCategoryByDependentTypeParams) => {
    const options = await apiOptions({
      url: `${API_DOCUMENT_TYPES}`,
      method: "get",
      headers: { [X_API_VERSION]: API_VERSION },
      filters: { dependentType: { eq: entityType } },
      sort,
      top,
      skip,
    });
    return apiRequest(options);
  },

  /**
   * Creates a new document category.
   *
   * @function create
   * @memberof documentCategories
   * @param {Object} params - Parameters for creating a document category.
   * @param {Object} params.data - The data for the new document category.
   * @returns {Promise<Object>} The response from the API.
   */
  create: async ({ data }: { data: ICreateDocumentCategoryRequest }) => {
    const options = await apiOptions({
      url: `${API_DOCUMENT_TYPES}`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    return apiRequest(options);
  },

  /**
   * Updates an existing document category.
   *
   * @function update
   * @memberof documentCategories
   * @param {Object} params - Parameters for updating a document category.
   * @param {Object} params.data - The data for the document category to update.
   * @returns {Promise<Object>} The response from the API.
   */
  update: async ({ data }: { data: IDocumentCategoriesResponse }) => {
    const options = await apiOptions({
      url: `${API_DOCUMENT_TYPES}/${data?.id}`,
      method: "patch",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    return apiRequest(options);
  },

  /**
   * Deletes a document category.
   *
   * @function delete
   * @memberof documentCategories
   * @param {Object} params - Parameters for deleting a document category.
   * @param {string} params.id - The ID of the document category to delete.
   * @param {string} params.modifiedOn - The modification timestamp.
   * @param {Object} [params.toastMessageConfig] - Optional configuration for toast messages.
   * @returns {Promise<Object>} The response from the API.
   */
  delete: async ({
    id,
    modifiedOn,
    toastMessageConfig,
  }: {
    id: string;
    modifiedOn: string;
    toastMessageConfig?: IToastMessageConfig;
  }) => {
    const options = await apiOptions({
      url: `${API_DOCUMENT_TYPES}/${id}`,
      method: "delete",
      data: { modifiedOn },
    });

    return apiRequest(options, toastMessageConfig);
  },
};

export interface IDocumentCategoryByDependentTypeParams extends IApiRequest {
  entityType: string;
}
